import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdBlock, MdCheckCircle, MdSave } from 'react-icons/md';

// Onglet « Règles de blocage » : parmi les réponses bloquées pour nos acheteurs (Locataire,
// Appartement…), celles que Batiweb accepte, et les produits pour lesquels un lead bloqué ne part
// jamais chez Batiweb. Pour les acheteurs rien ne change : ces leads restent bloqués.
// S'applique aux leads reçus ensuite ; les anciens se rattrapent dans « Bloqués historiques ».

interface BlockedField {
    id: string;
    name: string;
    blockedAnswers: string[];
}

interface BlockRules {
    acceptedAnswers: { field: string; answers: string[] }[];
    excludedSubIndustries: string[];
    updatedBy: string | null;
    updatedAt: string | null;
}

interface CatalogProduct {
    industry: string;
    subIndustry: string;
}

type Draft = { accepted: Record<string, string[]>; excluded: string[] };

const draftFromRules = (rules: BlockRules): Draft => ({
    accepted: Object.fromEntries(rules.acceptedAnswers.map((rule) => [rule.field, rule.answers])),
    excluded: rules.excludedSubIndustries,
});

const sameDraft = (a: Draft, b: Draft) => {
    const sorted = (list: string[] = []) => [...list].sort().join('|');
    const fields = new Set([...Object.keys(a.accepted), ...Object.keys(b.accepted)]);
    return sorted(a.excluded) === sorted(b.excluded) && Array.from(fields).every((f) => sorted(a.accepted[f]) === sorted(b.accepted[f]));
};

export default function BlockRulesTab() {
    const [fields, setFields] = useState<BlockedField[]>([]);
    const [products, setProducts] = useState<CatalogProduct[]>([]);
    const [rules, setRules] = useState<BlockRules | null>(null);
    const [draft, setDraft] = useState<Draft>({ accepted: {}, excluded: [] });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const fetchRules = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const res = await axios.get(`${CONFIG.apiUrl}/admin/batiweb/block-rules`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            setFields(res.data.fields);
            setProducts(res.data.subIndustries);
            setRules(res.data.rules);
            setDraft(draftFromRules(res.data.rules));
        } catch (e: any) {
            setError(e.response?.data?.message || 'Impossible de charger les règles de blocage.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRules();
    }, [fetchRules]);

    const dirty = !!rules && !sameDraft(draft, draftFromRules(rules));

    const productsByIndustry = useMemo(() => {
        const groups = new Map<string, string[]>();
        for (const p of products) groups.set(p.industry, [...(groups.get(p.industry) || []), p.subIndustry]);
        return Array.from(groups.entries());
    }, [products]);
    // Produit exclu absent du catalogue (renommé ou supprimé) : affiché pour pouvoir le retirer.
    const orphanExcluded = useMemo(
        () => draft.excluded.filter((name) => !products.some((p) => p.subIndustry === name)),
        [draft.excluded, products]
    );

    const toggleAnswer = (fieldId: string, answer: string) => {
        setSuccess('');
        setDraft((prev) => {
            const current = prev.accepted[fieldId] || [];
            const next = current.includes(answer) ? current.filter((a) => a !== answer) : [...current, answer];
            return { ...prev, accepted: { ...prev.accepted, [fieldId]: next } };
        });
    };

    const toggleExcluded = (subIndustry: string) => {
        setSuccess('');
        setDraft((prev) => ({
            ...prev,
            excluded: prev.excluded.includes(subIndustry) ? prev.excluded.filter((s) => s !== subIndustry) : [...prev.excluded, subIndustry],
        }));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setError('');
            setSuccess('');
            const res = await axios.put(
                `${CONFIG.apiUrl}/admin/batiweb/block-rules`,
                {
                    // Seuls les champs encore bloqués pour les acheteurs : une ancienne règle orpheline est abandonnée.
                    acceptedAnswers: fields
                        .map((f) => ({ field: f.id, answers: (draft.accepted[f.id] || []).filter((a) => f.blockedAnswers.includes(a)) }))
                        .filter((rule) => rule.answers.length > 0),
                    excludedSubIndustries: draft.excluded,
                },
                { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
            );
            setRules(res.data.rules);
            setDraft(draftFromRules(res.data.rules));
            setSuccess("Règles enregistrées : elles s'appliquent aux prochains leads bloqués.");
        } catch (e: any) {
            setError(e.response?.data?.message || "Erreur lors de l'enregistrement.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="py-10 text-center text-gray-400">Chargement...</div>;

    return (
        <>
            <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
                <p className="max-w-3xl text-sm text-gray-500 dark:text-gray-400">
                    Ces réponses restent bloquées pour nos acheteurs. Ici, vous choisissez celles que Batiweb accepte : un lead bloqué part chez
                    Batiweb seulement si <b>toutes</b> ses réponses bloquées sont acceptées et que son produit n'est pas exclu. S'applique aux
                    prochains leads ; les anciens se rattrapent dans l'onglet « Bloqués historiques ».
                </p>
                <div className="flex items-center gap-3">
                    {rules?.updatedAt ? (
                        <span className="text-xs text-gray-400">
                            Modifié le {new Date(rules.updatedAt).toLocaleDateString('fr-FR')}{rules.updatedBy ? ` par ${rules.updatedBy}` : ''}
                        </span>
                    ) : (
                        <span className="text-xs text-gray-400">Règles par défaut (jamais modifiées)</span>
                    )}
                    <button
                        onClick={handleSave}
                        disabled={!dirty || saving}
                        className="flex items-center gap-1 rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-40"
                    >
                        <MdSave />
                        {saving ? 'Enregistrement...' : 'Enregistrer'}
                    </button>
                </div>
            </div>

            {error && <div className="mb-4 rounded-lg bg-red-100 p-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">{error}</div>}
            {success && <div className="mb-4 rounded-lg bg-green-100 p-3 text-sm text-green-700 dark:bg-green-900/30 dark:text-green-400">{success}</div>}

            <h3 className="mb-3 text-sm font-bold uppercase text-gray-500">Réponses bloquées pour les acheteurs</h3>
            {fields.length === 0 ? (
                <div className="mb-8 text-sm text-gray-400">Aucune réponse bloquée n'est configurée sur les champs.</div>
            ) : (
                <div className="mb-8 grid grid-cols-1 gap-4 lg:grid-cols-2">
                    {fields.map((field) => (
                        <div key={field.id} className="rounded-xl border border-gray-200 p-4 dark:border-navy-600">
                            <div className="mb-3 font-semibold text-navy-700 dark:text-white">{field.name}</div>
                            <div className="flex flex-wrap gap-2">
                                {field.blockedAnswers.map((answer) => {
                                    const accepted = (draft.accepted[field.id] || []).includes(answer);
                                    return (
                                        <button
                                            key={answer}
                                            onClick={() => toggleAnswer(field.id, answer)}
                                            title={accepted ? 'Cliquer pour bloquer aussi pour Batiweb' : 'Cliquer pour envoyer ces leads à Batiweb'}
                                            className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${
                                                accepted
                                                    ? 'border-green-300 bg-green-100 text-green-700 dark:border-green-700 dark:bg-green-900/30 dark:text-green-300'
                                                    : 'border-gray-200 bg-gray-50 text-gray-500 dark:border-navy-600 dark:bg-navy-700 dark:text-gray-300'
                                            }`}
                                        >
                                            {accepted ? <MdCheckCircle /> : <MdBlock />}
                                            {answer}
                                            <span className="font-normal">{accepted ? '· envoyé à Batiweb' : '· bloqué'}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <h3 className="mb-1 text-sm font-bold uppercase text-gray-500">Produits exclus ({draft.excluded.length})</h3>
            <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                Pour ces produits, aucun lead bloqué ne part chez Batiweb, même avec des réponses acceptées.
            </p>
            {orphanExcluded.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                    {orphanExcluded.map((name) => (
                        <label key={name} className="flex items-center gap-2 rounded-lg bg-orange-50 px-2 py-1 text-xs text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
                            <input type="checkbox" checked onChange={() => toggleExcluded(name)} />
                            {name} (absent du catalogue)
                        </label>
                    ))}
                </div>
            )}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {productsByIndustry.map(([industry, subIndustries]) => (
                    <div key={industry} className="rounded-xl border border-gray-200 p-4 dark:border-navy-600">
                        <div className="mb-2 text-xs font-bold uppercase text-gray-500">{industry}</div>
                        <div className="flex flex-col gap-1">
                            {subIndustries.map((sub) => (
                                <label key={sub} className="flex items-center gap-2 text-sm text-navy-700 dark:text-white">
                                    <input type="checkbox" checked={draft.excluded.includes(sub)} onChange={() => toggleExcluded(sub)} />
                                    {sub}
                                </label>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
}
